import React, { useRef, useEffect } from "react";
import { useStore } from "@store/store";
import { Line } from 'react-chartjs-2';
import "chartjs-plugin-datalabels";
import "chartjs-plugin-zoom";
import moment from "moment-timezone";
import { colors } from "@utils/constant";

const SteppedLineChart = () => {
  const [state,] = useStore();
  const chartRef = useRef(null);
  const hoverState = useRef({ hoveredIndex: null });
  const MAX_Y_AXIS_VALUE = 100;
  const MIN_Y_AXIS_VALUE = 0;
  const minDate = (() => {
    const now = new Date();
    now.setMonth(now.getMonth() - 18);
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  const maxDate = (() => {
    const now = new Date();
    now.setMonth(now.getMonth() + 1);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  const minZoomRange = moment.duration(3, 'months');
  const last18MonthsDate = moment().subtract(18, 'months').startOf('day').format('YYYY-MM-DD HH:mm:ss');
  const dataFuelSurcharge = state?.customer?.fuelSurchargeEffectiveRateData || [];
  const listRates = dataFuelSurcharge.map(item => item.rate);
  listRates.push(listRates.at(-1));
  const listLabels = dataFuelSurcharge.map(item => last18MonthsDate > item.startDate ? last18MonthsDate : item.startDate);
  listLabels.push(
    moment(dataFuelSurcharge.at(-1)?.endDate).isValid()
      ? new Date(dataFuelSurcharge.at(-1)?.endDate) > new Date(maxDate)
        ? maxDate
        : dataFuelSurcharge.at(-1)?.endDate
      : maxDate
  );
  const maxRate = Math.floor(Math.max(...listRates, 0) + 1) > MAX_Y_AXIS_VALUE ? MAX_Y_AXIS_VALUE : Math.floor(Math.max(...listRates, 0) + 1);

  const data = {
    labels: listLabels || [],
    datasets: [
      {
        data: listRates || [],
        fill: false,
        borderColor: "transparent",
        steppedLine: true,
        lineTension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  // 👇 Plugin to draw custom labels between points
  const centerLabelPlugin = {
    id: 'centerLabel',
    afterDatasetsDraw: function (chart) {
      const ctx = chart.ctx;
      const yScale = chart.scales['y-axis-0'];
      const minY = yScale.getPixelForValue(MIN_Y_AXIS_VALUE);
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        const points = meta.data;

        ctx.save();
        ctx.font = '12px Arial';
        ctx.fillStyle = colors.gray1;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        for (let i = 0; i < points.length - 1; i++) {
          const curr = points[i];
          const next = points[i + 1];

          // Find midpoint horizontally
          const currCenter = curr.getCenterPoint();
          const nextCenter = next.getCenterPoint();
          const currValue = dataFuelSurcharge[i]?.rate;

          const midX = (currCenter.x + nextCenter.x) / 2;
          const midY = currValue > MIN_Y_AXIS_VALUE ? currCenter.y : minY;
          ctx.fillText(`${dataset.data[i].toFixed(2)}`, midX, midY - 10);
        }
        ctx.restore();
      });
    },
  };

  // Plugin to draw different colors for specific segments
  const segmentColorPlugin = {
    id: 'segmentColorPlugin',
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx;
      const meta = chart.getDatasetMeta(0);
      const points = meta.data;
      const yScale = chart.scales['y-axis-0'];
      const minY = yScale.getPixelForValue(MIN_Y_AXIS_VALUE);

      ctx.save();
      ctx.lineWidth = 2;

      for (let i = 0; i < points.length - 1; i++) {
        const curr = points[i];
        const next = points[i + 1];
        const currCenter = curr.getCenterPoint();
        const nextCenter = next.getCenterPoint();

        const currValue = dataFuelSurcharge[i]?.rate;
        const nextValue = dataFuelSurcharge[i + 1]?.rate;

        let horizontalColor = colors.primary;
        if (dataFuelSurcharge[i]?.isDefaultRate) {
          horizontalColor = colors.darkRed;
        }

        ctx.strokeStyle = horizontalColor;
        ctx.beginPath();
        ctx.moveTo(currCenter.x, currValue > MIN_Y_AXIS_VALUE ? currCenter.y : minY);
        ctx.lineTo(nextCenter.x, currValue > MIN_Y_AXIS_VALUE ? currCenter.y : minY);
        ctx.stroke();

        // vertical line to next point
        if (i < points.length - 2) {
          ctx.strokeStyle = "#e5e5e5";
          ctx.beginPath();
          ctx.moveTo(nextCenter.x, currValue > MIN_Y_AXIS_VALUE ? currCenter.y : minY);
          ctx.lineTo(nextCenter.x, nextValue > MIN_Y_AXIS_VALUE ? nextCenter.y : minY);
          ctx.stroke();
        }
      }

      ctx.restore();
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    title: {
      display: true,
      text: 'Fuel Surcharge - Application Timeline',
      fontSize: 18,
      fontStyle: 'bold',
      fontColor: colors.orange,
    },
    tooltips: { enabled: false },
    hover: {
      enabled: false
    },
    legend: {
      display: false,
    },
    scales: {
      xAxes: [
        {
          ticks: {
            autoSkip: false,
            min: minDate,
            max: maxDate,
            maxRotation: 35,
            minRotation: 20,
          },
          type: 'time',
          time: {
            parser: 'YYYY-MM-DD',
            unit: 'month',
            stepSize: 1,
            displayFormats: {
              month: 'MMM YYYY'
            },
          },
          scaleLabel: {
            display: true,
            labelString: 'Month',
            fontSize: 14,
            fontStyle: 'bold',
          },
        },
      ],
      yAxes: [
        {
          gridLines: { display: false },
          ticks: { min: MIN_Y_AXIS_VALUE, max: maxRate, stepSize: 1 },
          scaleLabel: {
            display: true,
            labelString: '%',
            fontSize: 20,
            fontStyle: 'bold'
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        display: false
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          rangeMin: {
            x: minDate,
          },
          rangeMax: {
            x: maxDate,
          },
          onPan: function ({ chart }) {
            clampZoomRange(chart);
          },
        },

        zoom: {
          enabled: true,
          drag: false,
          mode: 'x',
          rangeMin: {
            x: minDate,
          },
          rangeMax: {
            x: maxDate,
          },
          speed: 0.2,
          onZoom: function ({ chart }) {
            clampZoomRange(chart);
          },
        }
      },
    }
  };

  // Prevent going out of range
  function clampZoomRange(chart) {
    const xScale = chart.scales['x-axis-0'];
    const currentMin = moment(xScale.min);
    const currentMax = moment(xScale.max);

    let updateNeeded = false;

    if (currentMin.isBefore(minDate)) {
      xScale.options.ticks.min = minDate;
      updateNeeded = true;
    }
    if (currentMax.isAfter(maxDate)) {
      xScale.options.ticks.max = maxDate;
      updateNeeded = true;
    }

    if (updateNeeded) {
      chart.update();
    }

    const currentRange = moment.duration(currentMax.diff(currentMin));

    // if zoomed in closer than 3 months
    if (currentRange.asMilliseconds() < minZoomRange.asMilliseconds()) {
      // Find center of current view
      const center = moment(currentMin).add(currentRange.asMilliseconds() / 2, 'ms');

      // Set new limits around center so it's exactly 3 months wide
      const newMin = moment(center).subtract(minZoomRange.asMilliseconds() / 2, 'ms');
      const newMax = moment(center).add(minZoomRange.asMilliseconds() / 2, 'ms');

      xScale.options.ticks.min = newMin.isBefore(minDate) ? minDate : newMin;
      xScale.options.ticks.max = newMax.isAfter(maxDate) ? maxDate : newMax;

      chart.update();
    }
  }

  useEffect(() => {
    const chartInstance = chartRef.current && chartRef.current.chartInstance;
    if (!chartInstance) return;
    const tooltipEl = document.getElementById('custom-tooltip');
    const canvas = chartInstance.chart && chartInstance.chart.canvas;
    if (!canvas) return;
    const handleMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const meta = chartInstance.getDatasetMeta(0);
      const points = meta.data;
      let found = null;
      const yScale = chartInstance.chart.scales['y-axis-0'];
      const minY = yScale.getPixelForValue(MIN_Y_AXIS_VALUE);

      for (let i = 0; i < points.length - 1; i++) {
        const curr = points[i];
        const next = points[i + 1];
        const currCenter = curr.getCenterPoint();
        const nextCenter = next.getCenterPoint();
        const currValue = dataFuelSurcharge[i]?.rate;
        const minX = Math.min(currCenter.x, nextCenter.x);
        const maxX = Math.max(currCenter.x, nextCenter.x);

        const lineY = currValue > MIN_Y_AXIS_VALUE ? currCenter.y : minY;
        if (x >= minX && x <= maxX && Math.abs(y - lineY) < 6) {
          found = i;
          // Show HTML tooltip
          tooltipEl.style.display = 'block';
          tooltipEl.innerHTML = `
            <div style="text-align: center;">
              <p><span style="color: ${colors.skyblue};">Rate: </span><strong>${chartInstance.data.datasets[0].data[i].toFixed(2)}%</strong></p>
              <p><span style="color: ${colors.skyblue};">Start Date: </span><strong>${moment(dataFuelSurcharge[i]?.startDate).format("DD-MM-YYYY HH:mm:ss")} AEDT</strong></p>
              <p><span style="color: ${colors.skyblue};">End Date: </span><strong>${moment(dataFuelSurcharge[i]?.endDate).isValid()
              ? moment(dataFuelSurcharge[i]?.endDate).format("DD-MM-YYYY HH:mm:ss") + " AEDT"
              : dataFuelSurcharge[i]?.endDate
              }</strong></p>
            </div>
          `;
          tooltipEl.style.left = `${x}px`;
          tooltipEl.style.top = `${lineY - 10}px`;
          break;
        }
      }
      if (found === null) {
        tooltipEl.style.display = 'none';
      }

      if (hoverState.current.hoveredIndex !== found) {
        hoverState.current.hoveredIndex = found;
        chartInstance.update();
      }
    };
    const handleLeave = () => {
      hoverState.current.hoveredIndex = null;
      tooltipEl.style.display = 'none';
      chartInstance.update();
    };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseleave', handleLeave);

    return () => {
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (<div style={{ position: "relative", height: "640px", borderBottom: "1px solid #e3e3e3", paddingBottom: "20px", boxSizing: "content-box" }}>
    <Line ref={chartRef} data={data} options={options} plugins={[segmentColorPlugin, centerLabelPlugin]} />
    <div
      id="custom-tooltip"
      style={{
        position: 'absolute',
        display: 'none',
        background: '#fff',
        border: '1px solid #ccc',
        padding: '6px 10px',
        borderRadius: '4px',
        fontSize: '14px',
        pointerEvents: 'none',
        transform: 'translate(-50%, -100%)',
        whiteSpace: 'nowrap',
        transition: "opacity 0.1s ease",
        zIndex: 1000,
      }}
    ></div>
  </div>);
}

export default SteppedLineChart;
